import { ref, watch, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import kycApi from '@/api/modules/kycApi';
import relationshipTypesApi, { type ParecerRelationshipTypesSource } from '@/api/modules/relationshipTypes';

import { getRiskCategoryLabel } from '@/utils/kyc';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { usePreferencesStore } from '@/stores/preferences';
import type { KycCheck, Status as CheckStatus, KycReport, KycEntity } from '@/types/kyc';
import type { DecisionLogic } from '@/types/risk-profile';
import { useParecerQueries } from '@/composables/useParecerQueries';

// --- Interfaces ---

interface ResolvedProfile {
  source: 'none' | 'company' | 'default' | 'config';
  isDefault: boolean;
  checks: {
    originals: string[];
    unified: string[];
  };
}

export interface RiskProfileOption {
  id: string;
  name: string;
  description: string;
  is_active?: boolean;
  deleted_at?: string | null;
  can_request_report?: boolean;
  current_kyx_risk_model_instance_id?: string;
  current_kyx_risk_model_instance_version_number?: number | null;
  pricing_summary?: RiskProfilePricingSummary | null;
  has_policy_decision?: boolean;
  decision_logic?: DecisionLogic | null;
  decision_version?: number | null;
  reports: { request_id: string; report_generated_at: string; kyx_risk_model_instance_id?: string; risk_model_instance_version_number?: number | null }[];
  pending_report?: { request_id: string; status: string; started_at: string };
}

export interface RiskProfilePricingSummary {
  base_price: number;
  unit_variable_cost: number;
  free_entities: number;
}

interface UseKycDataProps {
  entityId: ComputedRef<string>;
  entityType: 'company' | 'person';
}

export function useKycData({ entityId, entityType }: UseKycDataProps) {
  const toast = useToast();
  const preferencesStore = usePreferencesStore();
  const { selectedRiskProfileId } = storeToRefs(preferencesStore);
  const selectedReportRequestId: Ref<string | null> = ref(null);
  const { getLatestActiveDecisionForEntity } = useParecerQueries();
  const hasInitializedSelection: Ref<boolean> = ref(false);

  const kycChecks: Ref<KycCheck[]> = ref([]);
  const loading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);
  const currentProfileHasReport: Ref<boolean> = ref(false);

  const entities: Ref<KycEntity[]> = ref([]);
  const entitiesLoading: Ref<boolean> = ref(false);

  const riskProfiles: Ref<RiskProfileOption[]> = ref([]);
  const riskProfilesLoading: Ref<boolean> = ref(false);

  // Relationship-type options ({value: id, label: name}) a parecer on the SELECTED risk policy
  // may be associated with, mirroring the manual parecer wizard's selector: the policy's own
  // relationship types, or the account's when the policy defines none (see
  // getParecerRelationshipTypes). Also offered in the auto-decision opt-in, so the user can
  // pre-pick which relationship types the future parecer will cover.
  const relationshipTypeOptions: Ref<{ value: string; label: string }[]> = ref([]);
  // Where the options came from — null until a fetch has answered for the current policy.
  const relationshipTypeOptionsSource: Ref<ParecerRelationshipTypesSource | null> = ref(null);
  const fetchRelationshipTypeOptions = async (): Promise<void> => {
    const riskProfileId = selectedRiskProfileId.value;
    relationshipTypeOptions.value = [];
    relationshipTypeOptionsSource.value = null;
    if (!riskProfileId) {
      return;
    }
    try {
      const { source, relationshipTypes } = await relationshipTypesApi.getParecerRelationshipTypes(
        riskProfileId,
        entityType,
      );
      relationshipTypeOptionsSource.value = source;
      relationshipTypeOptions.value = relationshipTypes.map((rt) => ({ value: rt.id, label: rt.name }));
    } catch {
      relationshipTypeOptions.value = [];
    }
  };
  const selectedRiskProfile = computed(() =>
    riskProfiles.value.find((profile) => profile.id === selectedRiskProfileId.value) ?? null,
  );
  const selectedRiskProfileDeleted = computed(() => !!selectedRiskProfile.value?.deleted_at);
  const selectedRiskProfileConfigRoute = computed<RouteLocationRaw | null>(() =>
    selectedRiskProfileId.value && !selectedRiskProfileDeleted.value
      ? { name: 'account-admin-profile-details', params: { id: selectedRiskProfileId.value } }
      : null,
  );

  // Computed: true if ANY profile has at least one report
  const anyProfileHasReports = computed(() =>
    riskProfiles.value.some(p => p.reports && p.reports.length > 0)
  );

  const categoryOrder: Ref<string[]> = ref([]);
  const reportGeneratedAt: Ref<string | null> = ref(null);
  const overallRiskLevelValue: Ref<number | null> = ref(null);

  // New state for async report generation
  const isGeneratingReport: Ref<boolean> = ref(false);
  const generationProgress: Ref<string> = ref('');
  const generationProgressLabel = computed(() => generationProgress.value || 'Gerando relatório...');
  const currentRequestId: Ref<string | null> = ref(null);

  const resolvedProfile: Ref<ResolvedProfile> = ref({ source: 'config', isDefault: false, checks: { originals: [], unified: [] } });
  const toReportTimestamp = (dateValue?: string | null) => {
    if (!dateValue) return 0;
    const parsed = new Date(dateValue).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sortReportsByDate = <T extends { report_generated_at?: string | null }>(reports: T[]): T[] =>
    [...reports].sort((a, b) => toReportTimestamp(b.report_generated_at) - toReportTimestamp(a.report_generated_at));

  const clearReportData = () => {
    selectedReportRequestId.value = null;
    kycChecks.value = [];
    entities.value = [];
    reportGeneratedAt.value = null;
    overallRiskLevelValue.value = null;
    currentProfileHasReport.value = false;
  };

  const processKycReport = (report: KycReport) => {
    categoryOrder.value = report.category_order || [];
    reportGeneratedAt.value = report.report_generated_at;
    overallRiskLevelValue.value = report.overall_risk_level_value ?? null;

    const sortedSummary = (report.summary || []).sort((a, b) => {
      const indexA = categoryOrder.value.indexOf(a.category_slug);
      const indexB = categoryOrder.value.indexOf(b.category_slug);
      if (indexA === -1 && indexB === -1) {
        const nameA = String(a.category_display_name ?? a.category_slug ?? '');
        const nameB = String(b.category_display_name ?? b.category_slug ?? '');
        return nameA.localeCompare(nameB, 'pt-BR');
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    kycChecks.value = sortedSummary.map((item, idx) => ({
      id: idx + 1,
      title: getRiskCategoryLabel(item.category_slug, item.category_display_name),
      category_slug: item.category_slug,
      entitiesChecked: item.entities_checked,
      entitiesFailed: item.entities_failed,
      status: item.status as CheckStatus,
      isOutdated: item.is_outdated,
      lastUpdated: report.report_generated_at?.toString() ?? new Date().toISOString(),
      highestRiskLevelValue: item.highest_risk_level_value,
      highestRiskLevel: item.highest_risk_level_text,
    }));

    entities.value = report.entities;

    const allOriginalChecks = new Set(report.entities.flatMap(e => e.categories.map(c => c.category_slug)));
    const allChecksList = Array.from(allOriginalChecks);

    resolvedProfile.value = {
      source: 'config',
      isDefault: false,
      checks: {
        originals: allChecksList,
        unified: allChecksList,
      },
    };
  };

  const fetchAndProcessExistingReport = async () => {
    if (!entityId.value || !selectedReportRequestId.value) return;

    loading.value = true;
    entitiesLoading.value = true;
    error.value = null;

    try {
      // This endpoint is meant to fetch *existing* data, not generate a new report.
      const response: KycReport = await kycApi.getKycReportByRequestId(selectedReportRequestId.value);
      processKycReport(response);
      currentProfileHasReport.value = true;
    } catch (e: any) {
      console.error('Failed to load existing KYC report:', e);
      error.value = 'Não foi possível carregar o relatório KYX existente';
      currentProfileHasReport.value = false; // Could fail if report expired or other issue
    } finally {
      loading.value = false;
      entitiesLoading.value = false;
    }
  };

  const fetchRiskProfilesWithReports = async (): Promise<void> => {
    if (!entityId.value) return;
    try {
      riskProfilesLoading.value = true;
      const profiles = await kycApi.getRiskProfilesWithReports(entityType, entityId.value);
      const normalizedProfiles = profiles.map(profile => ({
        ...profile,
        reports: sortReportsByDate(profile.reports || []),
      }));
      riskProfiles.value = normalizedProfiles;

      // Check for any pending reports first - resume polling if found
      const profileWithPending = normalizedProfiles.find(p => p.pending_report);
      if (profileWithPending && profileWithPending.pending_report) {
        selectedRiskProfileId.value = profileWithPending.id;
        isGeneratingReport.value = true;
        generationProgress.value = profileWithPending.pending_report.status === 'processing'
          ? 'Processando relatório...'
          : 'Na fila de processamento...';
        const pendingProfileReports = profileWithPending.reports || [];
        if (pendingProfileReports.length > 0) {
          const selectedReportStillValid = selectedReportRequestId.value
            ? pendingProfileReports.some((report: { request_id: string }) => report.request_id === selectedReportRequestId.value)
            : false;
          selectedReportRequestId.value = selectedReportStillValid
            ? selectedReportRequestId.value
            : pendingProfileReports[0]?.request_id ?? null;
          currentProfileHasReport.value = true;
        } else {
          clearReportData();
        }
        hasInitializedSelection.value = true;

        // Resume polling in background (don't block initialization)
        const pendingProfileName = profileWithPending.name;
        const pendingRequestId = profileWithPending.pending_report.request_id;
        pollForReportCompletion(pendingRequestId)
          .then(async reportData => {
            if (reportData) {
              processKycReport(reportData);
              currentProfileHasReport.value = true;
              toast.add({
                severity: 'success',
                summary: 'Relatório gerado',
                detail: `Relatório KYX criado usando o perfil "${pendingProfileName}".`,
                life: 5000,
              });
            }
            // Refresh profile/report list and force-select the completed request.
            await fetchRiskProfilesWithReports();
            const profileWithCompletedRequest = riskProfiles.value.find(profile =>
              profile.reports?.some(report => report.request_id === pendingRequestId)
            );
            if (profileWithCompletedRequest) {
              selectedRiskProfileId.value = profileWithCompletedRequest.id;
            }
            selectedReportRequestId.value = pendingRequestId;
          })
          .catch(() => {
            error.value = 'Não foi possível gerar o relatório KYX.';
            toast.add({
              severity: 'error',
              summary: 'Falha ao gerar relatório',
              detail: 'Ocorreu um erro ao gerar o relatório KYX. Tente novamente.',
              life: 5000,
            });
          })
          .finally(() => {
            isGeneratingReport.value = false;
            generationProgress.value = '';
          });

        return;
      }

      const profilesWithReports = normalizedProfiles.filter(p => p.reports && p.reports.length > 0);

      if (!hasInitializedSelection.value) {
        const activeDecision = await getLatestActiveDecisionForEntity(entityType, entityId.value);
        if (activeDecision) {
          const profileWithActiveDecision = normalizedProfiles.find(profile =>
            profile.reports?.some((report: { request_id: string }) => report.request_id === activeDecision.requestId)
          );
          if (profileWithActiveDecision) {
            selectedReportRequestId.value = activeDecision.requestId;
            selectedRiskProfileId.value = profileWithActiveDecision.id;
            currentProfileHasReport.value = true;
            hasInitializedSelection.value = true;
            return;
          }
        }
      }

      if (profilesWithReports.length > 0) {
        currentProfileHasReport.value = true;
        // Find the most recent report
        const mostRecentProfile = profilesWithReports.reduce((latest, current) => {
          const latestReportDate = latest.reports?.[0]?.report_generated_at;
          const currentReportDate = current.reports?.[0]?.report_generated_at;
          if (!latestReportDate || !currentReportDate) return latest;
          return new Date(latestReportDate) > new Date(currentReportDate) ? latest : current;
        });
        selectedRiskProfileId.value = mostRecentProfile.id;
        selectedReportRequestId.value = mostRecentProfile.reports?.[0]?.request_id ?? null;
      } else {
        clearReportData();
        // Select the first profile so user can generate a report
        selectedRiskProfileId.value = profiles.length > 0 ? profiles[0].id : null;
      }
      hasInitializedSelection.value = true;
    } catch (e: any) {
      console.error('Failed to load risk profiles with reports:', e);
      riskProfiles.value = [];
      selectedRiskProfileId.value = null;
      currentProfileHasReport.value = false;
    } finally {
      riskProfilesLoading.value = false;
    }
  };

  const POLL_INTERVAL_MS = 5000; // Poll every 5 seconds
  const MAX_POLL_ATTEMPTS = 60; // Max 3 minutes of polling (60 * 3s)
  const INITIAL_POLL_DELAY_MS = 30000; // Initial delay before polling for new reports (30 seconds)

  const pollForReportCompletion = async (requestId: string, initialDelayMs: number = 0): Promise<any> => {
    // Wait for initial delay if specified (used when we know report won't be ready immediately)
    if (initialDelayMs > 0) {
      generationProgress.value = 'Gerando relatório...';
      await new Promise(resolve => setTimeout(resolve, initialDelayMs));
    }

    let attempts = 0;

    while (attempts < MAX_POLL_ATTEMPTS) {
      attempts++;

      try {
        const statusResponse = await kycApi.getKycReportStatus(requestId);

        if (statusResponse.status === 'completed') {
          generationProgress.value = 'Relatório pronto';
          return statusResponse.report_data;
        }

        if (statusResponse.status === 'failed') {
          throw new Error('Falha ao gerar relatório');
        }

        // Still pending or processing - update progress and wait
        generationProgress.value = statusResponse.status === 'processing'
          ? 'Processando relatório...'
          : 'Na fila de processamento...';

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      } catch (e: any) {
        // If it's an intentional error (e.g., 'failed' status), stop polling immediately
        if (e?.message === 'Falha ao gerar relatório') {
          throw e;
        }
        // If it's a network error, keep trying until max attempts
        if (attempts >= MAX_POLL_ATTEMPTS) {
          throw e;
        }
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    }

    throw new Error('A geração do relatório expirou. Tente novamente.');
  };

  const requestNewReport = async (options?: { usePolicyDecision?: boolean; relationshipTypeIds?: string[] }) => {
    if (!entityId.value || !selectedRiskProfileId.value) {
      error.value = 'Selecione uma política para solicitar um novo relatório.';
      return;
    }

    const selectedProfile = riskProfiles.value.find(p => p.id === selectedRiskProfileId.value);
    if (selectedProfile?.can_request_report === false) {
      error.value = 'Esta política foi removida ou está indisponível para novos relatórios.';
      return;
    }

    const profileName = selectedProfile?.name || 'Política desconhecida';

    isGeneratingReport.value = true;
    generationProgress.value = 'Iniciando geração do relatório...';
    error.value = null;

    try {
      // Request new report - returns immediately with request_id
      const response = await kycApi.requestKycReport(
        entityType,
        entityId.value,
        selectedRiskProfileId.value,
        {
          forceRegenerate: true,
          usePolicyDecision: options?.usePolicyDecision,
          relationshipTypeIds: options?.relationshipTypeIds,
        }
      );
      currentRequestId.value = response.request_id;

      // If immediately completed (e.g., demo mode or cached), process it
      if (response.status === 'completed' && response.report_data) {
        processKycReport(response.report_data);
        currentProfileHasReport.value = true;
        await fetchRiskProfilesWithReports();
        toast.add({
          severity: 'success',
          summary: 'Relatório gerado',
          detail: `Relatório KYX criado usando o perfil "${profileName}".`,
          life: 5000,
        });
        return;
      }

      // If immediately failed, throw an error
      if (response.status === 'failed') {
        throw new Error('Falha ao gerar relatório');
      }

      // Poll for completion after initial delay (backend processing warm-up window).
      generationProgress.value = 'Gerando relatório...';
      const reportData = await pollForReportCompletion(response.request_id, INITIAL_POLL_DELAY_MS);

      if (reportData) {
        processKycReport(reportData);
        currentProfileHasReport.value = true;
        toast.add({
          severity: 'success',
          summary: 'Relatório gerado',
          detail: `Relatório KYX criado usando o perfil "${profileName}".`,
          life: 5000,
        });
      }

      // Refresh profile/report list and force-select the completed request.
      // This guarantees the new report is fetched even if status endpoint omits report_data.
      await fetchRiskProfilesWithReports();
      const profileWithCompletedRequest = riskProfiles.value.find(profile =>
        profile.reports?.some(report => report.request_id === response.request_id)
      );
      if (profileWithCompletedRequest) {
        selectedRiskProfileId.value = profileWithCompletedRequest.id;
      }
      selectedReportRequestId.value = response.request_id;
    } catch (e: any) {
      console.error('Failed to generate new KYC report:', e);
      // Rejections the backend can explain (an inactive/unknown CNPJ, an invalid CPF, insufficient
      // tokens) come back as a localized message — show it, so the user knows what to fix instead
      // of being told to retry something that will always fail.
      const message = getApiErrorMessage(
        e,
        'Ocorreu um erro ao gerar o relatório KYX. Tente novamente.'
      );
      error.value = message;
      toast.add({
        severity: 'error',
        summary: 'Falha ao gerar relatório',
        detail: message,
        life: 5000,
      });
    } finally {
      isGeneratingReport.value = false;
      generationProgress.value = '';
      currentRequestId.value = null;
    }
  };

  watch(selectedRiskProfileId, (newId, oldId) => {
    if (newId && newId !== oldId) {
      // Relationship options are scoped to the policy, so refresh them on profile change.
      fetchRelationshipTypeOptions();
      const selectedProfile = riskProfiles.value.find(p => p.id === newId);
      if (selectedProfile?.pending_report && isGeneratingReport.value) {
        if (selectedProfile.reports?.length) {
          const currentSelectedId = selectedReportRequestId.value;
          const hasSelectedReport = currentSelectedId
            ? selectedProfile.reports.some(report => report.request_id === currentSelectedId)
            : false;
          if (!hasSelectedReport) {
            selectedReportRequestId.value = selectedProfile.reports[0]?.request_id ?? null;
          }
          currentProfileHasReport.value = true;
        } else {
          clearReportData();
        }
        return;
      }
      if (selectedProfile && selectedProfile.reports && selectedProfile.reports.length > 0) {
        const currentSelectedId = selectedReportRequestId.value;
        const hasSelectedReport = currentSelectedId
          ? selectedProfile.reports.some(report => report.request_id === currentSelectedId)
          : false;
        // Update the selected report request ID to the most recent report for this profile
        if (!hasSelectedReport) {
          selectedReportRequestId.value = selectedProfile.reports[0]?.request_id ?? null;
        }
        currentProfileHasReport.value = true;
      } else {
        // If the newly selected profile has no report, clear the display
        clearReportData();
      }
    }
  });

  watch(selectedReportRequestId, (newId, oldId) => {
    if (newId && newId !== oldId) {
      currentProfileHasReport.value = true;
      fetchAndProcessExistingReport();
      return;
    }
    if (!newId) {
      clearReportData();
    }
  });

  watch(entityId, (newEntityId, oldEntityId) => {
    if (newEntityId && newEntityId !== oldEntityId) {
      hasInitializedSelection.value = false;
      initialize();
    }
  });

  const initialize = async () => {
    // Relationship options are policy-scoped, so fetch them only after the profile
    // selection has settled (fetchRiskProfilesWithReports sets selectedRiskProfileId).
    await fetchRiskProfilesWithReports();
    await fetchRelationshipTypeOptions();
  };

  return {
    kycChecks,
    loading,
    error,
    resolvedProfile,
    entities,
    entitiesLoading,
    riskProfiles,
    riskProfilesLoading,
    selectedRiskProfile,
    selectedRiskProfileDeleted,
    selectedRiskProfileConfigRoute,
    selectedRiskProfileId,
    selectedReportRequestId,
    categoryOrder,
    reportGeneratedAt,
    initialize,
    currentProfileHasReport,
    anyProfileHasReports,
    requestNewReport,
    relationshipTypeOptions,
    relationshipTypeOptionsSource,
    // New async generation state
    isGeneratingReport,
    generationProgress,
    generationProgressLabel,
    // Overall risk from the report
    overallRiskLevelValue,
  };
}
